// Joy 2026-07-02 — "when a duplicate is husband and wife, do I merge,
// or keep separate and then link them in the household section?" The
// couple-classified duplicate issue TOLD the steward to "link them as
// a household if they aren't already" but offered no button to do it —
// a soft "no dead ends" (UX rule #5) violation: the recommended next
// step was stated but not offered. This module is the pure planning
// half of the fix; DecideRail renders a "link as one household" action
// that fetches both contacts' households and routes through this plan.
//
// Root-safe on purpose (imports only the pure RecordRef type) so
// vitest runs it in the node pool without dragging web/src/api.ts's
// DOM-typed fetch / import.meta.env into the root tsconfig program —
// same convention as decide-rail-applicability.ts /
// household-cluster-predicate.ts.

import type { RecordRef } from "./household-cluster-predicate";

// Minimal subset of CleanupIssueSummary the offer predicate inspects
// (CleanupIssueSummary is a structural superset, so DecideRail passes
// its issue through unchanged).
export interface LinkableIssueLike {
  entityKind: string;
  issueFamily: string;
  affectedRecords?: Array<{ kind: string; id: string; label?: string }>;
}

// Offer "link as one household" only on a 2-contact duplicate cluster
// — the shape where "these are two PEOPLE (a couple), not one person"
// is a plausible answer. N≥3 clusters are intentionally excluded: the
// HouseholdClusterMergeOverlay this flow reuses handles only the
// 2-household shape, and a 3-way "which households combine?" flow
// isn't built.
export function canOfferLinkHousehold(issue: LinkableIssueLike): boolean {
  if (issue.entityKind !== "contact") return false;
  if (issue.issueFamily !== "duplicate") return false;
  const contacts = (issue.affectedRecords ?? []).filter(
    (r) => r.kind === "contact",
  );
  return contacts.length === 2;
}

// What DecideRail knows about each flagged contact after fetching its
// detail (household is null for a contact not linked to any household
// — the `contact-no-household` structure issue's shape).
export interface LinkHouseholdSide {
  contactLabel: string;
  household: {
    id: string;
    display_name: string;
    /** Member count — used only to propose the default survivor. */
    contact_count: number;
  } | null;
}

export type LinkHouseholdPlan =
  // One (or both) contacts has no household to merge — name who, so
  // the rail can explain the fix path instead of dead-ending.
  | { kind: "unlinked"; names: string[] }
  // Already one household — nothing to link; "keep separate" is the
  // whole answer.
  | { kind: "same"; householdLabel: string }
  // Two distinct households → hand these refs to the existing
  // household merge overlay (survivor pick + per-field review).
  | { kind: "link"; refs: RecordRef[]; defaultSurvivorId: string };

export function planLinkHouseholds(
  a: LinkHouseholdSide,
  b: LinkHouseholdSide,
): LinkHouseholdPlan {
  const unlinked = [a, b]
    .filter((s) => s.household == null)
    .map((s) => s.contactLabel);
  if (unlinked.length > 0) return { kind: "unlinked", names: unlinked };
  const hhA = a.household!;
  const hhB = b.household!;
  if (hhA.id === hhB.id) {
    return { kind: "same", householdLabel: hhA.display_name };
  }
  // Default survivor = the household with MORE members: it's likelier
  // the established record (spouse/kids/services already attached),
  // while a 1-member household is usually the import stub. Tie →
  // side A's household (the issue's first record). The steward picks
  // explicitly in the overlay either way — this is only the preselect.
  const defaultSurvivorId =
    hhB.contact_count > hhA.contact_count ? hhB.id : hhA.id;
  return {
    kind: "link",
    refs: [
      { kind: "household", id: hhA.id, label: hhA.display_name },
      { kind: "household", id: hhB.id, label: hhB.display_name },
    ],
    defaultSurvivorId,
  };
}
